import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { membershipCatalogService } from '../../services/membershipCatalogService';
import { productService } from '../../services/productService';
import { vehicleService } from '../../services/vehicleService';
import { armorTypesService } from '../../services/armorTypesService';
import { codeService } from '../../services/codeService';
import { giftCardService } from '../../services/giftCardService';
import { rateService } from '../../services/rateService';
import { currencyRateService } from '../../services/currencyRateService';
import { useVehicleClassesStore } from '../../stores/useVehicleClassesStore';
import { notify } from '../../assets/helpers/toast';
import {
  capitalize,
  clearObject,
  displayMembershipCode,
  filterById,
  filterParams,
  filterRCVOptions,
} from '../../assets/helpers/funciones';
import {
  fleetLoadRCVTypes,
  fleetLoadRCVTypesOptions,
  planAllowsUnlimitedTow,
  isNewPlanRulesActive,
  KMServiceOptions,
  LANDING,
  membershipCodes,
  MAX_MEMBERSHIP_QUANTITY_OPTIONS,
  codeTypeEnum,
  codeTypeOptions,
  rcvEnum,
  validateCodeEnum,
  vehiclesEnum,
  vehicleUsageEnum,
  vehicleUsageOptions,
  vehicleWeightOptions,
} from '../../assets/helpers/options';
import { discountCodeMaxLength, vintageYear } from '../../assets/helpers/const';
import { salesRoles } from '../../assets/helpers/roles';
import authService from '../../services/authService';
import { formatBs } from './useCurrencyConverter';
import * as schemas from '../../assets/schemas/schemas';

const CUSTOM_VEHICLE_ORDER_MAP = {
  AUTOMÓVIL: 1,
  CAMIONETA: 2,
  RÚSTICO: 3,
  MOTO: 4,
  TRIMOTO: 5,
  CAMIÓN: 6,
  AUTOBUS: 7,
  MINIBUS: 8,
  GRÚA: 9,
  AMBULANCIA: 10,
};

const QUOTE_STORAGE_KEY = 'membershipQuote';
const MAX_VEHICLES = MAX_MEMBERSHIP_QUANTITY_OPTIONS || 5;
const isDevelopEnv = ['true', true].includes(process.env.REACT_APP_DEVELOP);

const pickRandom = (list) =>
  list?.length ? list[Math.floor(Math.random() * list.length)] : null;

const getTodayISO = (date = new Date()) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

let vehicleSeq = 0;
const nextVehicleId = () => `veh-${Date.now()}-${vehicleSeq++}`;

const createEmptyVehicle = () => ({
  id: nextVehicleId(),
  year: '',
  classId: '',
  armorType: '',
  truckWeight: '',
  rcv: rcvEnum.RCV_STANDARD,
  vehicleUsage: '',
  km: '',
  useExtendedTow: false,
  useMedCert: false,
  hasRcvCard: false,
  membershipOptions: [],
  selectedMembershipId: '',
  rcvOptions: fleetLoadRCVTypesOptions,
  usageOptions: [],
  codeType: '',
  discountCode: null,
  giftCard: null,
  discountInput: '',
  isLoading: false,
  isApplyingDiscount: false,
  errors: {},
  codeErrors: {},
  preloadPlan: false,
});

const isGoldPlan = (code) =>
  [membershipCodes.GOLD, membershipCodes.GOLDM].includes(code);

// Cotizador: Classic/Plus/Gold (auto) y Classicm/Plusm/Goldm/Baratico/Bera (moto).
const QUOTE_ALLOWED_MEMBERSHIP_CODES = [
  membershipCodes.CLASSIC,
  membershipCodes.PLUS,
  membershipCodes.GOLD,
  membershipCodes.CLASSICM,
  membershipCodes.PLUSM,
  membershipCodes.GOLDM,
  membershipCodes.BARATICO,
  membershipCodes.BERA,
];

const filterQuoteMemberships = (memberships) =>
  [...(memberships || [])]
    .filter((item) => QUOTE_ALLOWED_MEMBERSHIP_CODES.includes(item?.code))
    .sort(
      (a, b) =>
        QUOTE_ALLOWED_MEMBERSHIP_CODES.indexOf(a.code) -
        QUOTE_ALLOWED_MEMBERSHIP_CODES.indexOf(b.code)
    );

const isTruckClassName = (className) =>
  [vehiclesEnum.CAMION, vehiclesEnum.AMBULANCIA, vehiclesEnum.GRUA].includes(
    className
  );

const usageOptionsForClass = (className) => {
  if (!className) return [];

  const pick = (...ids) =>
    vehicleUsageOptions.filter((option) => ids.includes(option._id));

  if (className === vehiclesEnum.AUTOMOVIL) {
    return pick(vehicleUsageEnum.PUBLIC, vehicleUsageEnum.PARTICULAR);
  }
  if (className === vehiclesEnum.RUSTICO) {
    return pick(
      vehicleUsageEnum.PUBLIC,
      vehicleUsageEnum.PARTICULAR,
      vehicleUsageEnum.LOAD
    );
  }
  if ([vehiclesEnum.AUTOBUS, vehiclesEnum.MINIBUS].includes(className)) {
    return pick(vehicleUsageEnum.PUBLIC);
  }
  if (className === vehiclesEnum.CAMIONETA) {
    return pick(
      vehicleUsageEnum.PUBLIC,
      vehicleUsageEnum.PARTICULAR,
      vehicleUsageEnum.LOAD
    );
  }
  if (className === vehiclesEnum.MOTO) {
    return pick(vehicleUsageEnum.LOAD, vehicleUsageEnum.PARTICULAR);
  }
  if ([vehiclesEnum.TRIMOTO, vehiclesEnum.AMBULANCIA].includes(className)) {
    return pick(vehicleUsageEnum.LOAD);
  }
  if ([vehiclesEnum.CAMION, vehiclesEnum.GRUA].includes(className)) {
    return pick(vehicleUsageEnum.LOAD, vehicleUsageEnum.TOW);
  }
  return vehicleUsageOptions;
};

const formatUsd = (value) => {
  const amount = Number.isFinite(value) ? value : 0;
  return `$${amount.toFixed(2)}`;
};

const formatQuoteDate = (isoDate) =>
  isoDate ? moment(isoDate).format('DD/MM/YYYY') : '';

const getDiscountLabel = (discountCode) => {
  if (!discountCode) return '';
  const amount = discountCode.discountNumber;
  const suffix = discountCode.discountType === 'Porcentaje' ? '%' : '$';
  const code = discountCode.code || '';
  return amount != null ? `${code} -${amount}${suffix}` : code;
};

const getGiftcardLabel = (giftCard) => {
  if (!giftCard) return '';
  const plan = giftCard?.membership?.code || '';
  const channel = giftCard?.channel || '';
  if (plan && channel) return `Tarjeta válida · ${plan} · ${channel}`;
  if (plan) return `Tarjeta válida · ${plan}`;
  return 'Tarjeta válida';
};

const buildMassivePricesPdfItem = (vehicle) => {
  const rcvOption = (vehicle.rcvOptions || []).find(
    (option) => option._id === vehicle.rcv
  );

  return clearObject({
    vehicle: clearObject({
      year: Number(vehicle.year),
      class: vehicle.classId,
      armored: Boolean(vehicle.armorType),
      vehicleUsage: vehicle.vehicleUsage,
    }),
    membership: clearObject({
      plan: vehicle.selectedMembership?._id || vehicle.selectedMembershipId,
      useExtendedTow: Boolean(vehicle.useExtendedTow),
      rcv: rcvOption?.mongoId,
      useMedCert: vehicle.useMedCert || undefined,
      hasRcvCard: vehicle.hasRcvCard || undefined,
    }),
    discountCode: vehicle.discountCode?._id,
    giftcard: vehicle.giftCard?._id,
  });
};

const buildQuoteMembershipItem = (vehicle, quoteDate, armorTypeOptions) => {
  const selected = vehicle.selectedMembership || {};
  const rcvOption = (vehicle.rcvOptions || []).find(
    (option) => option._id === vehicle.rcv
  );
  const armor = filterById(armorTypeOptions, vehicle.armorType);
  const {
    finalPriceBeforeDiscounts,
    originalPrice: initialValue,
    yearSurcharge,
    armorTypeSurcharge,
    classSurcharge,
    rcvSurcharge,
    discountByCode,
    finalPrice,
    extendedTowSurcharge,
    discountToRcv,
  } = selected;

  return clearObject({
    membershipId: selected._id,
    giftcard: vehicle.giftCard?._id,
    discountCode: vehicle.discountCode?._id,
    rcv: rcvOption?.mongoId,
    km: vehicle.km,
    truckWeight: vehicle.truckWeight,
    useExtendedTow: vehicle.useExtendedTow,
    vehicleUsage: vehicle.vehicleUsage,
    priceInfo: {
      finalPriceBeforeDiscounts,
      initialValue,
      yearSurcharge,
      armorTypeSurcharge,
      classSurcharge,
      rcvSurcharge,
      discountByGiftcard: vehicle.giftCard?.value || 0,
      discountByCode,
      finalPrice,
      extendedTowSurcharge,
      discountToRcv,
    },
    createdAt: moment(quoteDate).format('YYYY-MM-DD HH:mm:ss'),
    useMedCert: vehicle.useMedCert || false,
    hasRcvCard: vehicle.hasRcvCard || false,
    dataTable: {
      year: vehicle.year,
      class: capitalize(vehicle.className),
      classId: vehicle.classId,
      armorType: capitalize(armor?.name) || 'No',
      armorTypeId: vehicle.armorType,
      codeType: vehicle.codeType,
      code:
        vehicle.discountInput ||
        vehicle.discountCode?.code ||
        vehicle.giftCard?.code ||
        '',
      rcv: vehicle.rcv,
      plan: capitalize(displayMembershipCode(selected.code)),
    },
  });
};

const UI_TO_SCHEMA_FIELD = {
  year: 'year',
  classId: 'class',
  armorType: 'armorType',
  rcv: 'rcv',
  truckWeight: 'truckWeight',
  vehicleUsage: 'vehicleUsage',
  km: 'km',
};

const isAggregateYupMessage = (message) =>
  typeof message === 'string' && /\d+\s+errors?\s+occurred/i.test(message);

const firstYupMessage = (error) => {
  const candidates = [
    ...(error?.inner || []).map((item) => item?.message),
    ...(error?.errors || []),
    error?.message,
  ];
  return candidates.find((message) => message && !isAggregateYupMessage(message)) || '';
};

const collectYupErrors = (error) => {
  const errors = {};
  const details = error?.inner?.length ? error.inner : error ? [error] : [];
  details.forEach((item) => {
    const message = firstYupMessage(item) || item?.message;
    if (item?.path && message && !isAggregateYupMessage(message) && !errors[item.path]) {
      errors[item.path] = message;
    }
  });
  return errors;
};

const getClassName = (vehicleClasses, classId) =>
  filterById(vehicleClasses, classId)?.name || '';

const getSelectedMembership = (vehicle) =>
  vehicle.membershipOptions.find((option) => option._id === vehicle.selectedMembershipId) ||
  null;

/**
 * useMembershipQuote
 * ---------------------------------------------------------------------------
 * Lógica del cotizador de planes (misma fuente de precio que new-membership):
 *  - Catálogo de membresías según año, clase, RCV, uso y extras.
 *  - Tasa BCV atada a la fecha de cotización.
 *  - Códigos de descuento, copia, descarga y salto a generar orden.
 */
export default function useMembershipQuote() {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [quoteDate, setQuoteDate] = useState(getTodayISO());
  const [rate, setRate] = useState(0);
  const [isLoadingRate, setIsLoadingRate] = useState(false);
  const [vehicles, setVehicles] = useState([createEmptyVehicle()]);
  const [vehicleClasses, setVehicleClasses] = useState([]);
  const [armorTypeOptions, setArmorTypeOptions] = useState([
    { _id: '', name: 'No' },
  ]);
  const [copied, setCopied] = useState(false);
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
  const [quoteDateError, setQuoteDateError] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  const vehiclesRef = useRef(vehicles);
  vehiclesRef.current = vehicles;
  const quoteDateRef = useRef(quoteDate);
  quoteDateRef.current = quoteDate;
  const vehicleClassesRef = useRef(vehicleClasses);
  vehicleClassesRef.current = vehicleClasses;
  const armorTypeOptionsRef = useRef(armorTypeOptions);
  armorTypeOptionsRef.current = armorTypeOptions;
  const membershipRequestRef = useRef({});
  const yearTimersRef = useRef({});
  const fetchMembershipsRef = useRef(() => {});
  const downloadingRef = useRef(false);

  const userType = authService.getUserType();
  const canUseExtraFlags = userType && userType !== salesRoles.SALES_COUNTER;

  const fetchRate = useCallback(async (date) => {
    setIsLoadingRate(true);
    try {
      const isToday = !date || date === getTodayISO();

      if (!isToday) {
        const byDate = await currencyRateService.getCurrencyRates({
          currency: 'USD',
          date,
          limit: 1,
        });
        const byDateRate = byDate?.data?.result?.[0]?.rate;
        if (byDate?.status === 200 && byDateRate != null) {
          setRate(Number(byDateRate));
        } else {
          setRate(0);
          notify('No se encontró tasa BCV para la fecha seleccionada', 'error');
        }
        return;
      }

      const primary = await rateService.getDollarRate();
      const primaryRate = primary?.data?.rate;
      if (primary?.status === 200 && primaryRate != null) {
        setRate(Number(primaryRate));
        return;
      }

      const fallback = await currencyRateService.getCurrencyRates({
        currency: 'USD',
        date: '',
        limit: 1,
      });
      const fallbackRate = fallback?.data?.result?.[0]?.rate;
      if (fallback?.status === 200 && fallbackRate != null) {
        setRate(Number(fallbackRate));
        return;
      }

      notify('No se pudo obtener la tasa BCV', 'error');
    } catch (error) {
      notify('No se pudo obtener la tasa BCV', 'error');
    } finally {
      setIsLoadingRate(false);
    }
  }, []);

  const loadCatalogs = useCallback(async () => {
    setIsLoadingCatalogs(true);
    try {
      const { getCache, setCache } = useVehicleClassesStore.getState();
      const cachedData = getCache('vehicleClasses');

      if (cachedData?.classes) {
        setVehicleClasses(cachedData.classes);
      } else {
        const classesResponse = await vehicleService.getSalesVehicleClasses();
        if (classesResponse.status === 200) {
          const classes = [...(classesResponse?.data?.classes || [])].sort(
            (a, b) =>
              (CUSTOM_VEHICLE_ORDER_MAP[a.name] || 99) -
              (CUSTOM_VEHICLE_ORDER_MAP[b.name] || 99)
          );
          setCache(classes, 'vehicleClasses', classes.length);
          setVehicleClasses(classes);
        } else {
          notify(`${classesResponse.message}` || 'error', 'error');
        }
      }

      const armorResponse = await armorTypesService.getArmorTypes();
      if (armorResponse.status === 200) {
        setArmorTypeOptions([...(armorResponse?.data || []), { _id: '', name: 'No' }]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingCatalogs(false);
    }
  }, []);

  const patchVehicle = useCallback((id, patch) => {
    setVehicles((prev) =>
      prev.map((vehicle) => (vehicle.id === id ? { ...vehicle, ...patch } : vehicle))
    );
  }, []);

  const buildValidationData = useCallback((vehicle, quoteDateValue) => {
    const className = getClassName(vehicleClassesRef.current, vehicle.classId);
    const selected = getSelectedMembership(vehicle);
    return {
      year: String(vehicle.year || '').trim(),
      class: vehicle.classId || '',
      armorType: vehicle.armorType || '',
      createdAt: quoteDateValue || quoteDateRef.current || '',
      rcv: vehicle.rcv || '',
      truckWeight: vehicle.truckWeight || '',
      vehicleUsage: vehicle.vehicleUsage || '',
      km: vehicle.km ? String(vehicle.km) : '',
      selectedMembershipId: vehicle.selectedMembershipId || '',
      isTruck: isTruckClassName(className),
      // Con las nuevas condiciones el km del plan es fijo y no se elige
      isGold:
        isGoldPlan(selected?.code) &&
        !isNewPlanRulesActive(quoteDateValue || quoteDateRef.current),
    };
  }, []);

  const validateVehicle = useCallback((vehicle, { submitted = false } = {}) => {
    try {
      schemas.cotizadorVehicleSchema().validateSync(
        buildValidationData(vehicle, quoteDateRef.current),
        { abortEarly: false, context: { submitted } }
      );
      return {};
    } catch (error) {
      return collectYupErrors(error);
    }
  }, [buildValidationData]);

  const validateVehicleField = useCallback((vehicle, schemaField) => {
    try {
      schemas.cotizadorVehicleSchema().validateSyncAt(
        schemaField,
        buildValidationData(vehicle, quoteDateRef.current),
        { abortEarly: true }
      );
      return '';
    } catch (error) {
      return firstYupMessage(error);
    }
  }, [buildValidationData]);

  const validateQuoteDateValue = useCallback((value) => {
    return String(value || '').trim() ? '' : 'Obligatorio';
  }, []);

  const validateCodeFields = useCallback((vehicle) => {
    try {
      schemas.validateGiftcardSchema().validateSync(
        {
          codeType: vehicle.codeType || '',
          code: String(vehicle.discountInput || '').trim(),
        },
        { abortEarly: false }
      );
      return {};
    } catch (error) {
      return collectYupErrors(error);
    }
  }, []);

  const fetchRcvOptions = useCallback(async (id, overrides = {}) => {
    const vehicle = {
      ...(vehiclesRef.current.find((item) => item.id === id) || {}),
      ...overrides,
    };
    const membershipId =
      vehicle.selectedMembershipId || vehicle.membershipOptions?.[0]?._id;

    if (!membershipId) return;

    try {
      const params = {
        membership: membershipId,
        vehicleClass: vehicle.classId,
        truckWeight: vehicle.truckWeight,
        vehicleUsage: vehicle.vehicleUsage,
      };
      const response = await productService.getRCVs(filterParams(params));
      if (response.status !== 200) return;

      const data = filterRCVOptions([
        { _id: fleetLoadRCVTypes.N_A, name: fleetLoadRCVTypes.N_A },
        ...(response?.data || []).map((item) => ({
          _id: item?.name,
          name: item?.name,
          mongoId: item?._id,
          coverage: item?.prices?.coverage,
        })),
      ]);

      const nextRcv =
        data.find((option) => option._id === vehicle.rcv)?._id ||
        data.find((option) => option._id === rcvEnum.RCV_STANDARD)?._id ||
        data[0]?._id ||
        vehicle.rcv;
      const rcvChanged = Boolean(data.length) && nextRcv !== vehicle.rcv;

      patchVehicle(id, {
        rcvOptions: data.length ? data : fleetLoadRCVTypesOptions,
        ...(rcvChanged ? { rcv: nextRcv } : {}),
      });

      if (rcvChanged) {
        fetchMembershipsRef.current(id, { ...vehicle, rcv: nextRcv });
      }
    } catch (error) {
      console.error(error);
    }
  }, [patchVehicle]);

  const fetchMemberships = useCallback(
    async (id, overrides = {}) => {
      const vehicle = {
        ...(vehiclesRef.current.find((item) => item.id === id) || {}),
        ...overrides,
      };
      const year = String(vehicle.year || '');
      const classId = vehicle.classId;
      const yearError = validateVehicleField(vehicle, 'year');
      const classError = validateVehicleField(vehicle, 'class');

      if (year.length < 4 || !classId || yearError || classError) {
        patchVehicle(id, {
          membershipOptions: [],
          selectedMembershipId: '',
          preloadPlan: false,
          isLoading: false,
          errors: {
            ...(vehicle.errors || {}),
            ...(yearError ? { year: yearError } : {}),
            ...(classError ? { class: classError } : {}),
          },
        });
        return;
      }

      const requestId = (membershipRequestRef.current[id] || 0) + 1;
      membershipRequestRef.current[id] = requestId;
      patchVehicle(id, { isLoading: true });

      const params = {
        year,
        class: classId,
        truckWeight: vehicle.truckWeight,
        armorType: vehicle.armorType,
        discountCode: vehicle.discountCode?._id,
        giftCard: vehicle.giftCard?._id,
        rcv: vehicle.rcv || rcvEnum.RCV_STANDARD,
        vehicleUsage: vehicle.vehicleUsage,
        landing: LANDING.PRIVATE,
        km: vehicle.km,
        rcvEmissionDate: moment(quoteDateRef.current).format('YYYY-MM-DD'),
      };

      if (vehicle.useExtendedTow) params.useExtendedTow = vehicle.useExtendedTow;
      if (vehicle.useMedCert) params.useMedCert = vehicle.useMedCert;
      if (vehicle.hasRcvCard) params.hasRcvCard = vehicle.hasRcvCard;

      try {
        const response = await membershipCatalogService.getAvailableMemberships(
          filterParams(params)
        );
        if (membershipRequestRef.current[id] !== requestId) return;

          if (response.status === 200) {
            let data = filterQuoteMemberships(response?.data);
            const validGiftcard = data.some((item) => item?.discountByGiftCard);
            if (validGiftcard) {
              data = data.filter((item) => item?.discountByGiftCard);
            }
            let selectedId =
              data.find((option) => option._id === vehicle.selectedMembershipId)?._id ||
              '';
            let nextKm = vehicle.km;
            let nextUseExtendedTow = vehicle.useExtendedTow;
            let shouldRefetchForKm = false;

            if (vehicle.preloadPlan && data.length) {
              const picked = pickRandom(data);
              selectedId = picked?._id || '';
              const rulesDate = quoteDateRef.current;
              nextKm =
                isGoldPlan(picked?.code) && !isNewPlanRulesActive(rulesDate)
                  ? Number(vehicle.km) ||
                    pickRandom(KMServiceOptions)?._id ||
                    200
                  : '';
              nextUseExtendedTow = planAllowsUnlimitedTow(picked?.code, rulesDate)
                ? Math.random() < 0.3
                : false;
              shouldRefetchForKm =
                String(nextKm) !== String(vehicle.km || '') ||
                Boolean(nextUseExtendedTow) !== Boolean(vehicle.useExtendedTow);
            }

            const nextVehicle = {
              ...vehicle,
              membershipOptions: data,
              selectedMembershipId: selectedId,
              km: nextKm,
              useExtendedTow: nextUseExtendedTow,
              preloadPlan: false,
            };

            patchVehicle(id, {
              membershipOptions: data,
              selectedMembershipId: selectedId,
              km: nextKm,
              useExtendedTow: nextUseExtendedTow,
              preloadPlan: false,
              isLoading: false,
              errors: {
                ...(vehicle.errors || {}),
                year: '',
                class: '',
              },
            });
          fetchRcvOptions(id, nextVehicle);
          if (shouldRefetchForKm) {
            fetchMembershipsRef.current(id, nextVehicle);
          }
        } else {
          patchVehicle(id, { isLoading: false, membershipOptions: [] });
          notify(`${response.message}` || 'error', 'error');
        }
      } catch (error) {
        if (membershipRequestRef.current[id] !== requestId) return;
        patchVehicle(id, { isLoading: false });
        console.error(error);
      }
    },
    [fetchRcvOptions, patchVehicle, validateVehicleField]
  );

  fetchMembershipsRef.current = fetchMemberships;

  const refreshAllMemberships = useCallback(() => {
    vehiclesRef.current.forEach((vehicle) => {
      fetchMemberships(vehicle.id);
    });
  }, [fetchMemberships]);

  const handleQuoteDateChange = useCallback(
    (event) => {
      const value = event?.target?.value ?? '';
      setQuoteDate(value);
      quoteDateRef.current = value;
      setQuoteDateError(validateQuoteDateValue(value));
      if (value) {
        fetchRate(value);
        refreshAllMemberships();
      }
    },
    [fetchRate, refreshAllMemberships, validateQuoteDateValue]
  );

  const addVehicle = useCallback(() => {
    setVehicles((prev) => {
      if (prev.length >= MAX_VEHICLES) {
        notify(`Máximo ${MAX_VEHICLES} vehículos por cotización`, 'error');
        return prev;
      }
      return [...prev, createEmptyVehicle()];
    });
  }, []);

  const removeVehicle = useCallback(() => {
    setVehicles((prev) => (prev.length <= 1 ? prev : prev.slice(0, -1)));
  }, []);

  const setVehicleCount = useCallback((nextCount) => {
    const count = Math.min(Math.max(Number(nextCount) || 1, 1), MAX_VEHICLES);
    setVehicles((prev) => {
      if (count === prev.length) return prev;
      if (count > prev.length) {
        const extras = Array.from({ length: count - prev.length }, createEmptyVehicle);
        return [...prev, ...extras];
      }
      return prev.slice(0, count);
    });
  }, []);

  const handleVehicleFieldChange = useCallback(
    (id, field, value) => {
      const current = vehiclesRef.current.find((item) => item.id === id);
      if (!current) return;

      const className = getClassName(
        vehicleClassesRef.current,
        field === 'classId' ? value : current.classId
      );
      const patch = { [field]: value };

      if (field === 'classId') {
        const usageOptions = usageOptionsForClass(className);
        patch.usageOptions = usageOptions;
        patch.vehicleUsage = usageOptions.length === 1 ? usageOptions[0]._id : '';
        patch.truckWeight = '';
        patch.selectedMembershipId = '';
        patch.membershipOptions = [];
        patch.rcv = rcvEnum.RCV_STANDARD;
      }

      if (field === 'year') {
        patch.selectedMembershipId = '';
        patch.membershipOptions = [];
      }

      const nextVehicle = { ...current, ...patch };
      const schemaField = UI_TO_SCHEMA_FIELD[field];
      const nextErrors = { ...(current.errors || {}) };
      if (schemaField) {
        nextErrors[schemaField] = validateVehicleField(nextVehicle, schemaField);
      }
      if (field === 'classId') {
        nextErrors.class = validateVehicleField(nextVehicle, 'class');
        nextErrors.truckWeight = validateVehicleField(nextVehicle, 'truckWeight');
        nextErrors.vehicleUsage = validateVehicleField(nextVehicle, 'vehicleUsage');
      }
      if (field === 'rcv') {
        nextErrors.vehicleUsage = validateVehicleField(nextVehicle, 'vehicleUsage');
      }
      patch.errors = nextErrors;
      patchVehicle(id, patch);

      if (field === 'year') {
        clearTimeout(yearTimersRef.current[id]);
        yearTimersRef.current[id] = setTimeout(() => {
          fetchMemberships(id, nextVehicle);
        }, 400);
        return;
      }

      fetchMemberships(id, nextVehicle);
    },
    [fetchMemberships, patchVehicle, validateVehicleField]
  );

  const preloadRandomVehicle = useCallback(
    (id) => {
      if (!isDevelopEnv) return;

      const current = vehiclesRef.current.find((item) => item.id === id);
      if (!current) return;

      const classes = vehicleClassesRef.current || [];
      const klass = pickRandom(classes);
      if (!klass) return;

      const className = klass.name;
      const usageOptions = usageOptionsForClass(className);
      const usage = pickRandom(usageOptions);
      const armors = armorTypeOptionsRef.current || [];
      const armorChoices = armors.filter((option) => option._id);
      const armor =
        Math.random() < 0.75 || !armorChoices.length
          ? armors.find((option) => !option._id) || { _id: '' }
          : pickRandom(armorChoices);
      const min = vintageYear;
      const max = new Date().getFullYear() + 1;
      const year = min + Math.floor(Math.random() * (max - min + 1));
      const isTruck = isTruckClassName(className);
      const rcvChoices = (current.rcvOptions || []).filter((option) => option._id);
      const extras = canUseExtraFlags
        ? {
            useExtendedTow: false,
            useMedCert: Math.random() < 0.3,
            hasRcvCard: Math.random() < 0.3,
          }
        : {
            useExtendedTow: false,
            useMedCert: false,
            hasRcvCard: false,
          };

      const patch = {
        year,
        classId: klass._id,
        usageOptions,
        vehicleUsage: usage?._id || '',
        armorType: armor?._id || '',
        truckWeight: isTruck ? pickRandom(vehicleWeightOptions)?._id || '' : '',
        rcv: pickRandom(rcvChoices)?._id || rcvEnum.RCV_STANDARD,
        km: '',
        selectedMembershipId: '',
        membershipOptions: [],
        preloadPlan: true,
        errors: {},
        ...extras,
      };

      patchVehicle(id, patch);
      fetchMemberships(id, { ...current, ...patch });
    },
    [canUseExtraFlags, fetchMemberships, patchVehicle]
  );

  const handleVehicleBlur = useCallback(
    (id, field) => {
      const current = vehiclesRef.current.find((item) => item.id === id);
      if (!current) return;
      const schemaField = UI_TO_SCHEMA_FIELD[field];
      if (!schemaField) return;
      const message = validateVehicleField(current, schemaField);
      patchVehicle(id, {
        errors: { ...(current.errors || {}), [schemaField]: message },
      });
    },
    [patchVehicle, validateVehicleField]
  );

  const selectPlan = useCallback(
    (id, membership) => {
      const current = vehiclesRef.current.find((item) => item.id === id);
      if (!current) return;

      const alreadySelected = current.selectedMembershipId === membership?._id;
      const patch = alreadySelected
        ? { selectedMembershipId: '', km: '', useExtendedTow: false }
        : {
            selectedMembershipId: membership._id,
            // Nuevas condiciones: no se envía km (lo asigna el backend por plan)
            km:
              isGoldPlan(membership?.code) &&
              !isNewPlanRulesActive(quoteDateRef.current)
                ? current.km || 200
                : '',
            useExtendedTow: planAllowsUnlimitedTow(
              membership?.code,
              quoteDateRef.current
            )
              ? current.useExtendedTow
              : false,
          };

      const nextVehicle = { ...current, ...patch };
      patch.errors = {
        ...(current.errors || {}),
        selectedMembershipId: '',
        km: validateVehicleField(nextVehicle, 'km'),
      };
      patchVehicle(id, patch);
      fetchMemberships(id, nextVehicle);
    },
    [fetchMemberships, patchVehicle, validateVehicleField]
  );

  const handleCodeTypeChange = useCallback(
    (id, value) => {
      const current = vehiclesRef.current.find((item) => item.id === id);
      if (!current) return;
      const patch = {
        codeType: value,
        discountCode: value === codeTypeEnum.GIFTCARD ? null : current.discountCode,
        giftCard: value === codeTypeEnum.DISCOUNT ? null : current.giftCard,
        codeErrors: {
          ...(current.codeErrors || {}),
          codeType: value ? '' : 'Obligatorio',
        },
      };
      if (
        (value === codeTypeEnum.DISCOUNT && current.giftCard) ||
        (value === codeTypeEnum.GIFTCARD && current.discountCode)
      ) {
        patch.discountInput = current.discountInput;
      }
      patchVehicle(id, patch);
      fetchMemberships(id, { ...current, ...patch });
    },
    [fetchMemberships, patchVehicle]
  );

  const handleDiscountInputChange = useCallback(
    (id, value) => {
      const current = vehiclesRef.current.find((item) => item.id === id);
      if (!current) return;
      patchVehicle(id, {
        discountInput: String(value || '').toUpperCase(),
        codeErrors: { ...(current.codeErrors || {}), code: '' },
      });
    },
    [patchVehicle]
  );

  const applyDiscount = useCallback(
    async (id) => {
      const vehicle = vehiclesRef.current.find((item) => item.id === id);
      if (!vehicle) return;

      const codeErrors = validateCodeFields(vehicle);
      if (Object.keys(codeErrors).length) {
        patchVehicle(id, { codeErrors });
        return;
      }

      const code = String(vehicle.discountInput || '').trim().toUpperCase();
      patchVehicle(id, { isApplyingDiscount: true, codeErrors: {} });

      try {
        if (vehicle.codeType === codeTypeEnum.DISCOUNT) {
          const response = await codeService.validateCode(
            filterParams({ code, type: validateCodeEnum.NEW })
          );
          if ([200, 201].includes(response.status)) {
            const discountCode = {
              ...(response?.data || {}),
              code: response?.data?.code || code,
            };
            const patch = {
              discountCode,
              giftCard: null,
              discountInput: code,
              isApplyingDiscount: false,
              codeErrors: {},
            };
            patchVehicle(id, patch);
            fetchMemberships(id, { ...vehicle, ...patch });
            return;
          }
          patchVehicle(id, {
            isApplyingDiscount: false,
            codeErrors: {
              code:
                response.status === 404
                  ? 'Codigo de descuento no encontrado o expirado'
                  : response.message || 'Codigo de descuento no encontrado o expirado',
            },
          });
          return;
        }

        const response = await giftCardService.validateCode(
          filterParams({ code })
        );
        if ([200, 201].includes(response.status)) {
          const patch = {
            giftCard: response?.data,
            discountCode: null,
            discountInput: code,
            isApplyingDiscount: false,
            codeErrors: {},
          };
          patchVehicle(id, patch);
          fetchMemberships(id, { ...vehicle, ...patch });
          return;
        }
        patchVehicle(id, {
          isApplyingDiscount: false,
          codeErrors: {
            code:
              response.status === 404
                ? 'Giftcard no encontrada o expirada'
                : response.message || 'Giftcard no encontrada o expirada',
          },
        });
      } catch (error) {
        patchVehicle(id, {
          isApplyingDiscount: false,
          codeErrors: {
            code:
              vehicle.codeType === codeTypeEnum.GIFTCARD
                ? 'Error validando la giftcard'
                : 'Error validando el código de descuento',
          },
        });
      }
    },
    [fetchMemberships, patchVehicle, validateCodeFields]
  );

  const removeDiscount = useCallback(
    (id) => {
      const vehicle = vehiclesRef.current.find((item) => item.id === id);
      if (!vehicle) return;
      const patch = {
        discountCode: null,
        giftCard: null,
        discountInput: '',
        codeErrors: {},
      };
      patchVehicle(id, patch);
      fetchMemberships(id, { ...vehicle, ...patch });
    },
    [fetchMemberships, patchVehicle]
  );

  const pricedVehicles = useMemo(
    () =>
      vehicles.map((vehicle) => {
        const selected = getSelectedMembership(vehicle);
        const className = getClassName(vehicleClasses, vehicle.classId);
        const finalPrice = Number(selected?.finalPrice) || 0;
        const originalPrice =
          Number(
            selected?.specialOriginalPrice ||
              selected?.originalPrice ||
              finalPrice
          ) || finalPrice;
        const hasDiscount = Boolean(selected && originalPrice > finalPrice);
        return {
          ...vehicle,
          className,
          selectedMembership: selected,
          planName: capitalize(displayMembershipCode(selected?.code)),
          originalPrice,
          finalPrice,
          hasDiscount,
          discountLabel: getDiscountLabel(vehicle.discountCode),
          giftcardLabel: getGiftcardLabel(vehicle.giftCard),
          isTruck: isTruckClassName(className),
          isGold: isGoldPlan(selected?.code),
          allowsUnlimitedTow: planAllowsUnlimitedTow(selected?.code, quoteDate),
        };
      }),
    [quoteDate, vehicleClasses, vehicles]
  );

  const quotedVehicles = useMemo(
    () => pricedVehicles.filter((vehicle) => vehicle.selectedMembership),
    [pricedVehicles]
  );

  const canExportQuote = useMemo(
    () =>
      pricedVehicles.length > 0 &&
      pricedVehicles.every((vehicle) => Boolean(vehicle.selectedMembership)),
    [pricedVehicles]
  );

  const totalUsd = useMemo(
    () => quotedVehicles.reduce((sum, vehicle) => sum + (vehicle.finalPrice || 0), 0),
    [quotedVehicles]
  );

  const totalOriginalUsd = useMemo(
    () => quotedVehicles.reduce((sum, vehicle) => sum + (vehicle.originalPrice || 0), 0),
    [quotedVehicles]
  );

  const totalBs = useMemo(() => totalUsd * (rate || 0), [rate, totalUsd]);

  const buildQuoteText = useCallback(() => {
    const lines = ['🚗 Cotización Tu Gruero', ''];

    quotedVehicles.forEach((vehicle, index) => {
      const className = capitalize(vehicle.className) || 'Vehículo';
      const rcvName = capitalize(vehicle.rcv) || 'N/A';
      const planName = vehicle.planName || 'Plan';
      const finalBs = formatBs((vehicle.finalPrice || 0) * (rate || 0));

      lines.push(`Vehículo ${index + 1}`);
      lines.push(`${className} ${vehicle.year} · ${rcvName}`);

      if (vehicle.hasDiscount) {
        lines.push(
          `Plan ${planName}: ${formatUsd(vehicle.originalPrice)} → ${formatUsd(
            vehicle.finalPrice
          )} (Bs. ${finalBs})`
        );
      } else {
        lines.push(
          `Plan ${planName}: ${formatUsd(vehicle.finalPrice)} (Bs. ${finalBs})`
        );
      }

      if (vehicle.discountCode) {
        const amount = vehicle.discountCode.discountNumber;
        const discountValue =
          vehicle.discountCode.discountType === 'Porcentaje'
            ? `-${amount}%`
            : `-$${amount}`;
        lines.push(`🏷️ Descuento del ${discountValue}`);
      } else if (vehicle.giftCard) {
        const giftPlan = vehicle.giftCard?.membership?.code;
        lines.push(giftPlan ? `🏷️ Giftcard ${giftPlan}` : '🏷️ Giftcard');
      }

      lines.push('');
    });

    lines.push('━━━━━━━━━━');
    lines.push(`Total general: ${formatUsd(totalUsd)} (Bs. ${formatBs(totalBs)})`);
    lines.push(
      `Tasa BCV: ${rate ? Number(rate).toFixed(4) : '—'} Bs/USD · ${formatQuoteDate(
        quoteDate
      )}`
    );
    lines.push('');
    lines.push(
      '✅ El monto en Bs puede variar según la tasa BCV vigente al día de la afiliación'
    );

    return lines.join('\n');
  }, [quoteDate, quotedVehicles, rate, totalBs, totalUsd]);

  const handleCopy = useCallback(async () => {
    if (!canExportQuote) {
      notify('Seleccione un plan para todos los vehículos', 'error');
      return;
    }
    const value = buildQuoteText();
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      notify('Cotización copiada', 'success');
    } catch (error) {
      notify('No se pudo copiar la cotización', 'error');
    }
  }, [buildQuoteText, canExportQuote]);

  const handleDownload = useCallback(async () => {
    if (!canExportQuote) {
      notify('Seleccione un plan para todos los vehículos', 'error');
      return;
    }
    if (downloadingRef.current) return;

    const dateError = validateQuoteDateValue(quoteDate);
    setQuoteDateError(dateError);
    if (dateError) {
      notify('Seleccione la fecha de cotización', 'error');
      return;
    }

    const payload = {
      requestDate: quoteDate,
      items: quotedVehicles.map(buildMassivePricesPdfItem),
    };

    downloadingRef.current = true;
    setIsDownloading(true);
    try {
      const fileDate = formatQuoteDate(quoteDate).replace(/\//g, '-');
      const response = await membershipCatalogService.downloadMassivePricesPdf(
        payload,
        `cotizacion-planes-${fileDate || quoteDate}.pdf`
      );

      if (![200, 201].includes(response?.status)) {
        notify(response?.message || 'No se pudo descargar la cotización', 'error');
      }
    } catch (error) {
      console.error(error);
      notify('No se pudo descargar la cotización', 'error');
    } finally {
      downloadingRef.current = false;
      setIsDownloading(false);
    }
  }, [canExportQuote, quoteDate, quotedVehicles, validateQuoteDateValue]);

  const handleGenerateOrder = useCallback(() => {
    const dateError = validateQuoteDateValue(quoteDate);
    setQuoteDateError(dateError);

    let hasErrors = Boolean(dateError);
    const nextVehicles = vehiclesRef.current.map((vehicle) => {
      const errors = validateVehicle(vehicle, { submitted: true });
      if (Object.keys(errors).length) hasErrors = true;
      return { ...vehicle, errors };
    });
    setVehicles(nextVehicles);

    if (hasErrors) {
      notify('Revise los campos de la cotización', 'error');
      return;
    }

    const membershipList = pricedVehicles.map((vehicle) =>
      buildQuoteMembershipItem(vehicle, quoteDate, armorTypeOptions)
    );

    const payload = {
      quoteDate,
      rate,
      totalUsd,
      totalBs,
      membershipList,
    };

    try {
      sessionStorage.setItem(QUOTE_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      console.error(error);
    }

    setIsOpen(false);
    history.push(`/new-membership?fromQuote=${Date.now()}`);
  }, [
    armorTypeOptions,
    history,
    pricedVehicles,
    quoteDate,
    rate,
    totalBs,
    totalUsd,
    validateQuoteDateValue,
    validateVehicle,
  ]);

  const resetQuote = useCallback(() => {
    setVehicles([createEmptyVehicle()]);
    setQuoteDate(getTodayISO());
    setQuoteDateError('');
    setCopied(false);
  }, []);

  const openModal = useCallback(() => {
    const today = getTodayISO();
    setQuoteDate(today);
    setIsOpen(true);
    fetchRate(today);
    loadCatalogs();
  }, [fetchRate, loadCatalogs]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    resetQuote();
  }, [resetQuote]);

  useEffect(() => {
    if (!copied) return undefined;
    const timeoutId = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timeoutId);
  }, [copied]);

  useEffect(() => {
    const timers = yearTimersRef.current;
    return () => {
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  return {
    isOpen,
    openModal,
    closeModal,
    quoteDate,
    handleQuoteDateChange,
    quoteDateError,
    rate,
    rateFormatted: rate ? Number(rate).toFixed(4) : '',
    isLoadingRate,
    isLoadingCatalogs,
    vehicles: pricedVehicles,
    vehicleCount: vehicles.length,
    addVehicle,
    removeVehicle,
    setVehicleCount,
    handleVehicleFieldChange,
    preloadRandomVehicle,
    canPreloadVehicle: isDevelopEnv,
    handleVehicleBlur,
    selectPlan,
    handleCodeTypeChange,
    handleDiscountInputChange,
    applyDiscount,
    removeDiscount,
    codeTypeOptions,
    vehicleClasses,
    armorTypeOptions,
    vehicleWeightOptions,
    kmOptions: KMServiceOptions,
    // true = nuevas condiciones (km fijo por plan, sin avisos de recargo por año/blindaje)
    newPlanRules: isNewPlanRulesActive(quoteDate),
    maxYear: new Date().getFullYear() + 1,
    minYear: vintageYear,
    maxVehicles: MAX_VEHICLES,
    canUseExtraFlags,
    copied,
    handleCopy,
    handleDownload,
    isDownloading,
    handleGenerateOrder,
    quotedCount: quotedVehicles.length,
    canExportQuote,
    totalUsd,
    totalUsdFormatted: formatUsd(totalUsd),
    totalOriginalUsd,
    totalHasDiscount: quotedVehicles.some((vehicle) => vehicle.hasDiscount),
    totalBs,
    totalBsFormatted: formatBs(totalBs),
    todayISO: getTodayISO(),
    codeMaxLength: discountCodeMaxLength,
  };
}
